import React, { useState, useEffect, useRef } from "react"
import { Loader2, ChevronDown } from "lucide-react"
import { Input } from "src/components/ui/input"
import { Textarea } from "src/components/ui/textarea"
import { Button } from "src/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "src/components/ui/dropdown-menu"
import { FormItem, FormLabel, FormControl } from "src/components/ui/form"
import { CATEGORIES, CATEGORY_MAP, STATUSES, STATUS_MAP } from "../constants"
import { VariableInserter } from "./VariableInserter"
import { MessagePreview } from "./MessagePreview"

const EMPTY = { name: "", category: "lead", status: "active", subject: "", body: "" }

export function TemplateForm({ template, channel, onSubmit, onCancel, isPending }) {
  const isEdit = !!template
  const [form, setForm] = useState(EMPTY)
  const bodyRef = useRef(null)

  useEffect(() => {
    setForm(
      template
        ? {
            name: template.name || "",
            category: template.category || "lead",
            status: template.status || "active",
            subject: template.subject || "",
            body: template.body || "",
          }
        : EMPTY
    )
  }, [template])

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }))

  // Insert a variable token at the cursor position in the body textarea.
  function insertVariable(token) {
    const el = bodyRef.current
    if (!el) {
      set("body", `${form.body}${token}`)
      return
    }
    const start = el.selectionStart ?? form.body.length
    const end = el.selectionEnd ?? form.body.length
    const next = form.body.slice(0, start) + token + form.body.slice(end)
    set("body", next)
    requestAnimationFrame(() => {
      el.focus()
      const pos = start + token.length
      el.setSelectionRange(pos, pos)
    })
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (!form.name.trim() || !form.body.trim()) return
    onSubmit({ ...form, channel })
  }

  const isEmail = channel === "email"

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
      <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
        <div className="grid grid-cols-2 gap-3">
          <FormItem>
            <FormLabel>Template name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Site visit confirmation" value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus />
            </FormControl>
          </FormItem>
          <FormItem>
            <FormLabel>About (category)</FormLabel>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" className="w-full justify-between font-normal text-slate-700">
                  {CATEGORY_MAP[form.category]?.label || "Lead"}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                <DropdownMenuRadioGroup value={form.category} onValueChange={(v) => set("category", v)}>
                  {CATEGORIES.map((c) => (
                    <DropdownMenuRadioItem key={c.value} value={c.value}>{c.label}</DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </FormItem>
        </div>

        {isEmail && (
          <FormItem>
            <FormLabel>Subject</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Your visit to {{ project.name }}" value={form.subject} onChange={(e) => set("subject", e.target.value)} />
            </FormControl>
          </FormItem>
        )}

        <div className="grid grid-cols-[1fr,200px] gap-3">
          <FormItem>
            <FormLabel>Message body</FormLabel>
            <FormControl>
              <Textarea
                ref={bodyRef}
                rows={8}
                placeholder="Hi {{ lead.first_name }}, …"
                value={form.body}
                onChange={(e) => set("body", e.target.value)}
                className="font-mono text-[13px]"
              />
            </FormControl>
            <p className="mt-1 text-[11px] text-slate-400">
              Use <span className="font-mono">{"{{ variable }}"}</span> tokens — click a variable to insert it.
            </p>
          </FormItem>
          <FormItem>
            <FormLabel>Variables</FormLabel>
            <div className="h-[200px]">
              <VariableInserter category={form.category} onInsert={insertVariable} />
            </div>
          </FormItem>
        </div>

        <FormItem>
          <FormLabel>Preview</FormLabel>
          <MessagePreview channel={channel} category={form.category} subject={form.subject} body={form.body} />
        </FormItem>

        <FormItem>
          <FormLabel>Status</FormLabel>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" className="w-48 justify-between font-normal text-slate-700">
                {STATUS_MAP[form.status]?.label || "Active"}
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuRadioGroup value={form.status} onValueChange={(v) => set("status", v)}>
                {STATUSES.map((s) => (
                  <DropdownMenuRadioItem key={s.value} value={s.value}>{s.label}</DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </FormItem>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isPending}>Cancel</Button>
        <Button type="submit" disabled={isPending || !form.name.trim() || !form.body.trim()} className="gap-2">
          {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {isEdit ? "Save changes" : "Create template"}
        </Button>
      </div>
    </form>
  )
}
